import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  AlertDescription,
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RadioGroup,
  RadioGroupItem,
} from "@repo/ui";
import { PlusIcon } from "lucide-react";
import { useEffect } from "react";
import { useFieldArray, useForm, type UseFormReturn } from "react-hook-form";

import PropertyPurchaseCard from "@/components/property-purchase-card";
import { SearchableSelect } from "@/components/searchable-select";
import {
  EXPO_48_BANKS,
  EXPO_48_CHARACTERISTICS,
  EXPO_48_INTERESTS,
  EXPO_48_PRICE_RANGES,
  EXPO_48_PURCHASE_YEARS,
  formatPhoneNumber,
} from "@/lib/expo-48-constants";
import {
  createExpo48Schema,
  type Expo48FormData,
  type Expo48FormInput,
} from "@/lib/expo-48-schema";
import { BANGKOK_DISTRICTS, isBangkok, THAILAND_PROVINCES } from "@/lib/thailand-locations";

interface PropertyPurchaseMetadata {
  id: number;
  coupon_ready_at: string | null;
  coupons_printed_at: string | null;
}

interface Expo48FormProps {
  mode: "register" | "edit" | "admin";
  requireCouponFields?: boolean;
  defaultValues?: Partial<Expo48FormInput>;
  onSubmit: (data: Expo48FormData) => void | Promise<void>;
  children?: React.ReactNode;
  propertyPurchasesMetadata?: PropertyPurchaseMetadata[];
}

export default function Expo48Form({
  mode,
  requireCouponFields = false,
  defaultValues,
  onSubmit,
  children,
  propertyPurchasesMetadata = [],
}: Expo48FormProps) {
  const schema = createExpo48Schema(mode, requireCouponFields);

  const form = useForm<Expo48FormData>({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: {
      name: "",
      surname: "",
      phone: "",
      email: "",
      age: "",
      gender: "",
      interests: [],
      interestOther: "",
      banks: [],
      bankOther: "",
      propertyCharacteristics: "",
      priceRange: "",
      locationData: {
        province: "",
        district: "",
      },
      purchaseYear: "",
      purchases: requireCouponFields
        ? [
            {
              purchasePrice: "",
              developer: "",
              fullAddress: "",
              purchasedPropertyType: "",
              purchasedPropertyCharacteristics: "",
            },
          ]
        : [],
      ...defaultValues,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "purchases",
  });

  // Update form when defaultValues change (for edit/admin modes)
  useEffect(() => {
    if (defaultValues && (mode === "edit" || mode === "admin")) {
      form.reset({
        name: "",
        surname: "",
        phone: "",
        email: "",
        age: "",
        gender: "",
        interests: [],
        interestOther: "",
        banks: [],
        bankOther: "",
        propertyCharacteristics: "",
        priceRange: "",
        locationData: {
          province: "",
          district: "",
        },
        purchaseYear: "",
        purchases: requireCouponFields
          ? [
              {
                purchasePrice: "",
                developer: "",
                fullAddress: "",
                purchasedPropertyType: "",
                purchasedPropertyCharacteristics: "",
              },
            ]
          : [],
        ...defaultValues,
      });
    }
  }, [defaultValues, mode, form, requireCouponFields]);

  const watchInterests = form.watch("interests");
  const watchBanks = form.watch("banks");
  const watchProvince = form.watch("locationData.province");

  // Standard form layout for all modes
  return (
    <Form {...form}>
      <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name ชื่อ</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="ชื่อ" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="surname"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Surname นามสกุล</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="นามสกุล" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone เบอร์โทรศัพท์มือถือ</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    inputMode="tel"
                    placeholder="080-000-0000"
                    onChange={(e) => {
                      const formatted = formatPhoneNumber(e.target.value);
                      field.onChange(formatted);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email อีเมล</FormLabel>
                <FormControl>
                  <Input {...field} type="email" placeholder="hello@gmail.com" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="age"
            render={({ field }) => (
              <FormItem className="self-start">
                <FormLabel>Age อายุ</FormLabel>
                <FormControl>
                  <Input {...field} type="number" inputMode="numeric" placeholder="32" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="gender"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Gender เพศ</FormLabel>
                <FormControl>
                  <RadioGroup onValueChange={field.onChange} value={field.value}>
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="male" id="gender-male" />
                      <label htmlFor="gender-male" className="text-sm font-normal cursor-pointer">
                        Male / ชาย
                      </label>
                    </div>
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="female" id="gender-female" />
                      <label htmlFor="gender-female" className="text-sm font-normal cursor-pointer">
                        Female / หญิง
                      </label>
                    </div>
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="prefer_not_to_answer" id="gender-prefer-not" />
                      <label
                        htmlFor="gender-prefer-not"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Prefer not to answer / ไม่ระบุ
                      </label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Interests */}
        <FormField
          control={form.control}
          name="interests"
          render={() => (
            <FormItem>
              <FormLabel>Interest ความสนใจภายในงาน</FormLabel>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {EXPO_48_INTERESTS.map((opt) => (
                  <FormField
                    key={opt.value}
                    control={form.control}
                    name="interests"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value?.includes(opt.value)}
                            onCheckedChange={(checked) => {
                              return checked
                                ? field.onChange([...field.value, opt.value])
                                : field.onChange(
                                    field.value?.filter((value) => value !== opt.value)
                                  );
                            }}
                          />
                        </FormControl>
                        <FormLabel className="text-sm font-normal">{opt.label}</FormLabel>
                      </FormItem>
                    )}
                  />
                ))}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        {watchInterests?.includes("other") && (
          <FormField
            control={form.control}
            name="interestOther"
            render={({ field }) => (
              <FormItem>
                <FormLabel>โปรดระบุ Others</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="propertyCharacteristics"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Characteristics of interest ลักษณะที่สนใจ</FormLabel>
              <FormControl>
                <RadioGroup onValueChange={field.onChange} value={field.value}>
                  {EXPO_48_CHARACTERISTICS.map((opt) => (
                    <div key={opt.value} className="flex items-center gap-3">
                      <RadioGroupItem value={opt.value} id={`char-${opt.value}`} />
                      <label
                        htmlFor={`char-${opt.value}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {opt.label}
                      </label>
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="priceRange"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Price range of interest ช่วงราคาที่อยู่อาศัยที่สนใจ</FormLabel>
              <FormControl>
                <RadioGroup onValueChange={field.onChange} value={field.value}>
                  {EXPO_48_PRICE_RANGES.map((opt) => (
                    <div key={opt.value} className="flex items-center gap-3">
                      <RadioGroupItem value={opt.value} id={`price-${opt.value}`} />
                      <label
                        htmlFor={`price-${opt.value}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {opt.label}
                      </label>
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="locationData.province"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Location of interest ทำเลที่สนใจ</FormLabel>
              <FormControl>
                <SearchableSelect
                  items={THAILAND_PROVINCES}
                  value={field.value || ""}
                  onChange={field.onChange}
                  placeholder="เลือกจังหวัด / Select Province"
                  searchPlaceholder="ค้นหาจังหวัด..."
                  emptyText="ไม่พบจังหวัด"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {watchProvince && isBangkok(watchProvince) && (
          <FormField
            control={form.control}
            name="locationData.district"
            render={({ field }) => (
              <FormItem className="pl-4">
                <FormLabel>District of interest เขตที่สนใจ</FormLabel>
                <FormControl>
                  <SearchableSelect
                    items={BANGKOK_DISTRICTS}
                    value={field.value || ""}
                    onChange={field.onChange}
                    placeholder="เลือกเขต / Select District"
                    searchPlaceholder="ค้นหาเขต..."
                    emptyText="ไม่พบเขต"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="purchaseYear"
          render={({ field }) => (
            <FormItem>
              <FormLabel>When are you planning on purchasing? ระยะเวลาที่วางแผนจะซื้อ</FormLabel>
              <FormControl>
                <RadioGroup onValueChange={field.onChange} value={field.value}>
                  {EXPO_48_PURCHASE_YEARS.map((opt) => (
                    <div key={opt.value} className="flex items-center gap-3">
                      <RadioGroupItem value={opt.value} id={`year-${opt.value}`} />
                      <label
                        htmlFor={`year-${opt.value}`}
                        className="text-sm font-normal cursor-pointer"
                      >
                        {opt.label}
                      </label>
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Banks */}
        <FormField
          control={form.control}
          name="banks"
          render={() => (
            <FormItem>
              <FormLabel>Bank of interest สินเชื่อจากสถาบันการเงินที่สนใจ</FormLabel>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {EXPO_48_BANKS.map((bank) => (
                  <FormField
                    key={bank}
                    control={form.control}
                    name="banks"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value?.includes(bank)}
                            onCheckedChange={(checked) => {
                              return checked
                                ? field.onChange([...field.value, bank])
                                : field.onChange(field.value?.filter((value) => value !== bank));
                            }}
                          />
                        </FormControl>
                        <FormLabel className="text-sm font-normal">{bank}</FormLabel>
                      </FormItem>
                    )}
                  />
                ))}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        {watchBanks?.includes("อื่นๆ / Other") && (
          <FormField
            control={form.control}
            name="bankOther"
            render={({ field }) => (
              <FormItem>
                <FormLabel>โปรดระบุธนาคาร Others</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {/* Property Purchases */}
        {(requireCouponFields || mode === "admin") && (
          <div className="space-y-4 border-t pt-4">
            <p className="text-sm font-medium">Purchase Information ข้อมูลการซื้อ</p>

            {fields.map((field, index) => {
              const metadata = propertyPurchasesMetadata[index];
              return (
                <PropertyPurchaseCard
                  key={field.id}
                  form={form}
                  index={index}
                  onRemove={() => remove(index)}
                  canRemove={fields.length > 1 || !requireCouponFields}
                  propertyPurchaseId={metadata?.id}
                  couponReadyAt={metadata?.coupon_ready_at}
                  couponsPrintedAt={metadata?.coupons_printed_at}
                  onPrint={
                    metadata?.id
                      ? () => window.open(`/admin/print-coupon/${metadata.id}`, "_blank")
                      : undefined
                  }
                />
              );
            })}

            <Button
              type="button"
              variant="outline"
              onClick={() =>
                append({
                  purchasePrice: "",
                  developer: "",
                  fullAddress: "",
                  purchasedPropertyType: "",
                  purchasedPropertyCharacteristics: "",
                })
              }
            >
              <PlusIcon className="size-4" />
              Add Purchase
            </Button>
          </div>
        )}

        {Object.keys(form.formState.errors).length > 0 && (
          <Alert variant="destructive">
            <AlertDescription>
              กรุณากรอกข้อมูลให้ครบถ้วน / Please fill in all required fields
            </AlertDescription>
          </Alert>
        )}

        {children}
      </form>
    </Form>
  );
}

// Export form type for use in parent components
export type { Expo48FormData, UseFormReturn };
